import { useState, useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useHistory, useParams, generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Notification, Prompt, Spin, Layout } from 'Components';
import GroupsLayoutTemplate from 'Pages/Groups/GroupsLayoutTemplate';
import { useGroupsHierarchyContext } from './GroupsHierarchyContext';
import { AddMultipleGroupsMenu } from 'Pages/Groups/components';
import {
	HierarchyGroup,
	HierarchyValues,
	RouteParams
} from './GroupsHierarchy.types';
import HierarchyGroupComponent from './HierarchyGroup';
import {
	prepareFormValues,
	createHierarchyInput
} from './GroupsHierarchy.helpers';
import { CREATE_HIERARCHY } from 'Services/Api/Groups/Mutations';
import { GET_GROUPS_LIST } from 'Services/Api/Groups/Queries';
import {
	CreateHierarchyGroupsRequest,
	CreateHierarchyGroupsResponse,
	GetGroupsListResponse
} from 'Services/Api/Groups/Types';
import { getAppRoutes } from 'Pages/App/App.constants';
import { checkApolloError } from 'Helpers/graphql';
import { APIErrorCodes, ErrorInfo } from 'app-types';
import {
	DataTreeContextProps,
	DataTreeNode
} from 'Contexts/DataTree/DataTree.types';
import {
	bulkAssignNestedHierarchyLabel,
	getHierarchyLabel
} from 'Services/Api/HierarchyLabel/HierarchyLabelService';
import { HierarchyLabelType, getAllowedChildLabels, isLeafLabel } from 'app-config';
import { apolloClient } from 'Services/Api/client';
import { Group } from 'Services/Api/Groups/Types';

const GroupsHierarchyManageLayout = () => {
	const { t } = useTranslation();
	const [createHierarchy, { loading }] = useMutation<
		CreateHierarchyGroupsResponse,
		CreateHierarchyGroupsRequest
	>(CREATE_HIERARCHY);
	const { hierarchyGroups, blockIsUniqError, parent } = useGroupsHierarchyContext();
	const { groupId } = useParams<RouteParams>();
	const history = useHistory();
	const [selectedItem, setSelectedItem] = useState<DataTreeNode[]>([
		{
			key: (hierarchyGroups[0]?.id as unknown) as number,
			title: hierarchyGroups[0]?.name
		}
	]);

	const [reloadingFieldArray, setReloadingFieldArray] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const form = useForm({
		defaultValues: prepareFormValues(hierarchyGroups),
		mode: 'all'
	});

	const [{ key, title }] = selectedItem;

	const group = useMemo(
		() =>
			hierarchyGroups.find((group) => group.id === key) as HierarchyGroup,
		[key, hierarchyGroups]
	);

	const handleOnHierarchyGroupSubmit = useCallback(
		async (values: HierarchyValues) => {
			if (isSubmitting) {
				return;
			}

			// ── Validate hierarchy label parent-child relationships before submission ──
			const allFormGroups = Object.values(values).flat();
			const validationErrors: string[] = [];

			// First, check that ALL groups have a hierarchyLabel
			const groupsWithoutLabels = allFormGroups.filter(
				(g) => g.name && !g.hierarchyLabel
			);
			if (groupsWithoutLabels.length > 0) {
				setIsSubmitting(false);
				Notification.error({
					description: (
						<div>
							<strong>Missing hierarchy labels:</strong>
							<p style={{ marginTop: 8 }}>
								The following groups are missing a Type selection:
							</p>
							<ul style={{ marginTop: 8, paddingLeft: 20 }}>
								{groupsWithoutLabels.map((g, idx) => (
									<li key={idx}>"{g.name}" (Level {g.level})</li>
								))}
							</ul>
							<p style={{ marginTop: 8 }}>Please select a Type for all groups before saving.</p>
						</div>
					),
					duration: 10
				});
				return;
			}

			// Build a map of group id → hierarchyLabel for quick lookup
			const groupLabelMap = new Map<string, string>();
			allFormGroups.forEach((g) => {
				if (g.hierarchyLabel) {
					groupLabelMap.set(g.id, g.hierarchyLabel);
				}
			});

			// Get the parent group's hierarchy label (the route parent group)
			// For level-2 groups, their parent is the route groupId (parent prop)
			let routeParentLabel: string | undefined;
			try {
				const parentLabelRes = await getHierarchyLabel(Number(groupId));
				if (parentLabelRes.success && parentLabelRes.data?.node_type) {
					routeParentLabel = parentLabelRes.data.node_type;
				}
			} catch (err) {
				console.warn('Could not fetch parent hierarchy label for validation:', err);
			}

			// For each group with a label, validate against its parent's label
			for (const group of allFormGroups) {
				if (!group.hierarchyLabel || !group.name) continue;

				// Find parent label
				let parentLabel: string | undefined;
				if (group.parentId) {
					// If parentId is the route groupId, use the fetched parent label
					if (group.parentId === groupId) {
						parentLabel = routeParentLabel;
					} else {
						// Parent is another form group
						parentLabel = groupLabelMap.get(group.parentId);
					}
				}

				// Validate the child label is allowed under the parent
				const allowedLabels = getAllowedChildLabels(parentLabel);
				if (!allowedLabels.includes(group.hierarchyLabel as HierarchyLabelType)) {
					const parentGroup = allFormGroups.find((g) => g.id === group.parentId);
					const parentName = parentGroup?.name || parent?.name || 'parent';
					const parentLabelDisplay = parentLabel || 'unknown';
					validationErrors.push(
						`"${group.name}" (${group.hierarchyLabel}) cannot be a child of "${parentName}" (${parentLabelDisplay}). Allowed types: ${allowedLabels.join(', ')}.`
					);
				}

				// Validate leaf nodes don't have children
				if (isLeafLabel(group.hierarchyLabel)) {
					const hasChildren = allFormGroups.some((child) => child.parentId === group.id);
					if (hasChildren) {
						validationErrors.push(
							`"${group.name}" is labeled as "${group.hierarchyLabel}" (leaf node) but has child groups. Leaf nodes cannot have children.`
						);
					}
				}
			}

			if (validationErrors.length > 0) {
				setIsSubmitting(false);
				Notification.error({
					description: (
						<div>
							<strong>Invalid hierarchy label configuration:</strong>
							<ul style={{ marginTop: 8, paddingLeft: 20 }}>
								{validationErrors.map((err, idx) => (
									<li key={idx}>{err}</li>
								))}
							</ul>
						</div>
					),
					duration: 10
				});
				return;
			}

			setIsSubmitting(true);
			try {
				const input = createHierarchyInput(values, groupId);

				await createHierarchy({
					variables: {
						input
					}
				});

				// Collect hierarchy labels from form values (excluding groups without labels)
				const allFormGroups = Object.values(values).flat();
				const groupsWithLabels = allFormGroups.filter(
					(g) => g.hierarchyLabel && g.name
				);

				if (groupsWithLabels.length > 0) {
					try {
						// Fetch the newly created groups (with real IDs) including nested levels
						const { data: groupsData } = await apolloClient.query<GetGroupsListResponse>({
							query: GET_GROUPS_LIST,
							fetchPolicy: 'network-only',
							variables: {
								filters: {
									parentId: Number(groupId),
									depth: 6
								}
							}
						});

						const createdGroups = groupsData?.listGroups;

						if (createdGroups && createdGroups.length > 0) {
							// Derive rootGroupId from the fetched groups themselves — they
							// already carry the correct rootGroupId from the listGroups query.
							// Fall back to tenantId (from GET_GROUP) or groupId as last resort.
							const rootGroupId =
								createdGroups[0].rootGroupId ??
								(Number(parent?.tenantId) || Number(groupId));

							// ── Build a UUID → real-ID map so we can match nested children ──
							// Form groups use temporary UUIDs for id/parentId.
							// Backend groups use real numeric ids.
							// We process level-by-level: first match level-2 groups (whose
							// parentId is the route groupId), then level-3 groups (whose
							// real parentId we now know from the level-2 mapping), etc.
							const uuidToRealId = new Map<string, number>();
							// The route groupId is the parent for all level-2 form groups
							uuidToRealId.set(String(groupId), Number(groupId));

							// All form groups (including those without labels) — we need
							// them all to build the parent mapping for deeper levels.
							const everyFormGroup = Object.values(values).flat();

							// Sort by level so we process parents before children
							const sortedFormGroups = [...everyFormGroup].sort(
								(a, b) => a.level - b.level
							);

							// Track which backend groups have already been claimed
							// so that two form groups with the same name+level under
							// different parents don't both grab the same backend row.
							const claimedBackendIds = new Set<number>();

							for (const fg of sortedFormGroups) {
								// Resolve the form group's parent UUID to a real ID
								const realParentId = fg.parentId
									? uuidToRealId.get(fg.parentId)
									: Number(groupId);

								// Find the backend group that matches name + level + real parentId
								const match = createdGroups.find(
									(cg: Group) =>
										cg.name === fg.name &&
										cg.level === fg.level &&
										Number(cg.parentId) === realParentId &&
										!claimedBackendIds.has(Number(cg.id))
								);

								if (match) {
									uuidToRealId.set(fg.id, Number(match.id));
									claimedBackendIds.add(Number(match.id));
								}
							}

							// Now build the label assignments using the resolved mapping
							const nodeTypesMap = new Map<number, {
								id: number;
								name: string;
								parentId: number | null;
								rootGroupId: number;
								level: number;
								nodeType: HierarchyLabelType;
							}>();

							for (const fg of sortedFormGroups) {
								if (!fg.hierarchyLabel) continue;
								const realId = uuidToRealId.get(fg.id);
								if (!realId) continue;

								const backendGroup = createdGroups.find(
									(cg: Group) => Number(cg.id) === realId
								);
								if (!backendGroup) continue;

								nodeTypesMap.set(realId, {
									id: realId,
									name: backendGroup.name,
									parentId: backendGroup.parentId,
									rootGroupId: backendGroup.rootGroupId ?? rootGroupId,
									level: backendGroup.level,
									nodeType: fg.hierarchyLabel as HierarchyLabelType
								});
							}

							const nodeTypes = Array.from(nodeTypesMap.values());

							if (nodeTypes.length > 0) {
								const labelResult = await bulkAssignNestedHierarchyLabel(
									{
										rootGroupId,
										nodeTypes
									}
								);

								if (labelResult && !labelResult.success) {
									console.error(
										'Failed to assign hierarchy labels:',
										labelResult.message
									);
									Notification.error({
										description:
											labelResult.message ||
											'Hierarchy was created but labels could not be assigned. Please edit the groups to retry.'
									});
								}
							}
						}
					} catch (labelError) {
						console.error(
							'Failed to assign hierarchy labels:',
							labelError
						);
						Notification.error({
							description:
								'Hierarchy was created but labels could not be assigned. Please edit the groups to retry.'
						});
					}
				}

			Notification.success({
				description: t('groupsHierarchy.successNotification')
			});
			window.unblock?.();
			history.push(
				generatePath(getAppRoutes().GROUPS, { groupId })
			);
			} catch (error) {
				setIsSubmitting(false);
				const apolloError = checkApolloError(error);

				if (apolloError.is(APIErrorCodes.UnprocessableEntity)) {
					const metaKeys = apolloError.getMeta<
						Record<string, unknown>
					>();

					if (metaKeys) {
						const keys = Object.keys(metaKeys);

						const valuesLevelsIds = Object.keys(values);

						const pathToTranslation =
							keys.length > 1 ? 'multiple' : 'single';
						Notification.error({
							description: t(
								`groups.errorCodes.${APIErrorCodes.GroupNameShouldBeUniqueAtLevel}.${pathToTranslation}`
							)
						});
						valuesLevelsIds.forEach((levelId) => {
							values[levelId].forEach((value, index) => {
								if (keys.includes(value.id)) {
									form.setError(
										`${levelId}.${index}.name`,
										{}
									);
								}
							});
						});
					}
          return
				}
				if (apolloError.is(APIErrorCodes.GroupCannotBeProperty)) {
					Notification.error({
						description: t(
							`groupsHierarchy.notifications.error.${APIErrorCodes.GroupCannotBeProperty}`
						)
					});
          return
				}

        if(apolloError.is(APIErrorCodes.GroupSyncCodeShouldBeUniqPerTenant)){
					const metaKeys = apolloError.getMeta<
						{rowNumber: number; levelId: string;}[]
					>();

					if (metaKeys?.length) {

						metaKeys.forEach(({levelId, rowNumber}) => {
              form.setError(
                `${levelId}.${rowNumber}.syncCode`,
                {}
              );
						});
					}
          Notification.error({
            description: t('group.fieldErrorCodes.GROUP_SYNC_CODE_SHOULD_BE_UNIQUE_PER_TENANT', {path: 'Group sync code'})
          });

          return 
        }
				if (apolloError.isApolloError()) {
					const errorInfo = apolloError.getErrorInfo<
						Record<string, ErrorInfo[]>
					>();

					if (errorInfo) {
						Notification.error({
							description: t(
								'groupsHierarchy.notifications.error.submitHierarchy'
							)
						});
						return;
					}
					Notification.error({
						description: t('errorCodes.genericErrorMessage')
					});
				}
			}
		},

		// eslint-disable-next-line
		[groupId, history, form, isSubmitting]
	);

	const onHierarchyGroupSubmit = useCallback(
		(ev) => {
			form.handleSubmit(handleOnHierarchyGroupSubmit)(ev);
		},
		[form, handleOnHierarchyGroupSubmit]
	);

	const onCancel = useCallback(() => {
		// react-router-dom v5 uses history v4 at runtime (goBack), but
		// the root history v5 types expose back(). Suppress the mismatch.
		// eslint-disable-next-line
		(history as any).goBack();
	}, [history]);

	const onChange: DataTreeContextProps['onChange'] = (value) => {
		if (value) {
			setSelectedItem(value);
			form.reset(form.getValues());
			setReloadingFieldArray(true);
			setTimeout(() => {
				setReloadingFieldArray(false);
			}, 50);
		}
	};

	return (
		<>
			<Prompt blockTransition />
			<FormProvider {...form}>
				<GroupsLayoutTemplate<HierarchyGroup>
					onChange={onChange}
					dataSource={hierarchyGroups}
					initialValue={selectedItem}
					defaultActiveExpand
					header={() => (
						<AddMultipleGroupsMenu
							onCancel={onCancel}
							onSubmit={onHierarchyGroupSubmit}
							loading={loading || isSubmitting}
							headerTranslationPath="groupsHierarchy.title"
							disabledSubmit={blockIsUniqError || isSubmitting}
						/>
					)}
				>
					{reloadingFieldArray ? (
						<Layout>
							<Spin />
						</Layout>
					) : (
						<HierarchyGroupComponent
							fieldArrayName={String(key)}
							defaultGroupName={title as string}
							group={group}
						/>
					)}
				</GroupsLayoutTemplate>
			</FormProvider>
		</>
	);
};

export default GroupsHierarchyManageLayout;
